import { useTranslation } from '@utils/i18n/i18n';
import { Icon } from '@nazka/nazka.mapframe.components';
import { faClose } from '@fortawesome/pro-light-svg-icons';
import { useStation, useVehicle, useAddress } from '@services/query.service';
import Button from '@App/components/Button/Button';
import {
  faArrowRotateLeft, faBatteryBolt, faArrowsRotate, faShuffle,
  faHandshake, faGraduationCap, faGasPump, faEuroSign,
} from '@fortawesome/pro-regular-svg-icons';
import { providersOptions } from '@App/pages/Map/Sidepanel/sidepanel.constants';

import {
  usePropulsionOptions,
  usePropulsionNotUsedOptions,
  useTransmissionOptions,
} from '@App/pages/Map/Sidepanel/sidepanel.utils';

import {
  formatNumber, getFormFactor, useFormFactorText, useReturnContraintsText,
  getStationVehicleTypes, minutesToNiceString,
} from './popup.utils';

import './popup.styl';

function Popup({ feature, onRemove }) {
  const { t, lang } = useTranslation();
  const formFactorText = useFormFactorText();
  const returnContraintsText = useReturnContraintsText();

  const propulsionOptions = usePropulsionOptions();
  const propulsionNotUsedOptions = usePropulsionNotUsedOptions();
  const transmissionOptions = useTransmissionOptions();

  const { data: vehicle, isLoading: vehicleIsLoading } = useVehicle(
    feature?.properties?.vehicle_id,
    feature?.properties?.operator,
    getFormFactor(feature),
  );

  const { data: station, isLoading: stationIsLoading } = useStation(
    feature?.properties?.station_id,
    feature?.properties?.operator,
  );

  const {
    data: address,
    isLoading: addressIsLoading,
    error: addressError,
  } = useAddress(...(feature?.geometry?.coordinates || []), lang);

  const { properties } = vehicle || station || {};
  const vehicleTypes = getStationVehicleTypes(feature, properties);
  const bluebikeVehicleTypes = vehicleTypes.filter(vehicleType => (
    vehicleType?.form_factor && Number.isFinite(Number(vehicleType?.availability))
  ));
  const isBluebikeStation = properties?.operator === 'bluebike'
    && properties?.capacity != null
    && bluebikeVehicleTypes.length > 0;
  const totalAvailability = bluebikeVehicleTypes.reduce(
    (sum, vehicleType) => sum + Number(vehicleType.availability),
    0,
  );
  const getBluebikeCountLabel = (vehicleType) => {
    if (vehicleType?.form_factor === 'cargo_bicycle') return t('popup_bluebike_cargo-bikes');
    if (vehicleType?.propulsion_type === 'human') return t('popup_bluebike_city-bikes');
    if (vehicleType?.propulsion_type === 'electric_assist') return t('popup_bluebike_e-bikes');
    return formFactorText?.[vehicleType?.form_factor]?.multi || vehicleType?.form_factor;
  };
  const getReturnIcon = (returnConstraint) => {
    if (returnConstraint === 'any_station') return faArrowsRotate;
    if (returnConstraint === 'free_floating') return faShuffle;
    return faArrowRotateLeft;
  }

  let url
  if (properties?.operator && vehicleTypes[0]?.form_factor) {
    url = t(`url_${properties?.operator}_${vehicleTypes[0]?.form_factor}`);
    url = url.startsWith('http') ? url : `https://${url}`;
  }
  return (
    <div className={`popup${isBluebikeStation ? ' popup--bluebike' : ''}${(vehicleIsLoading || stationIsLoading || (addressIsLoading && !addressError)) ? ' is-loading' : ''}`}>
      {properties && (
        <>
          <div className="popup__header">
            <div className="popup__header__title">
              <Icon className="popup__header__title__icon" icon={`/img/icons/providers-full-color/${properties?.operator}.svg`} />
              <div className="popup__header__title__text">
                <h4 className="popup__header__title__text__main">
                  {(feature?.properties?.marker_counter >= 0) && (
                    <p className="popup__header__title__text__main__badge">
                      <span>
                        {feature?.properties?.marker_counter}
                      </span>
                    </p>
                  )}
                  {providersOptions.find(({ id }) => id === properties?.operator)?.label}
                </h4>
                {!addressIsLoading && (
                  (['degage', 'cozywheels'].includes(properties?.operator) && (
                    <p className="popup__header__title__text__sub">
                      {properties?.commune}
                    </p>
                  )) || (properties?.address && (
                    <p className="popup__header__title__text__sub">
                      {`${properties?.address}${properties?.commune ? `, ${properties.commune}` : ''}`}
                    </p>
                  )) || (address && (
                    <p className="popup__header__title__text__sub">
                      {address}
                    </p>
                  )) || (properties?.commune && (
                    <p className="popup__header__title__text__sub">
                      {properties?.commune}
                    </p>
                  ))
                )}
              </div>
            </div>
            <Button
              className="popup__header__button"
              onClick={onRemove}
              icon={faClose}
              layout="ghost"
              rootElProps={{ title: t('popup_close_button_tooltip') }}
            />
          </div>
          <div className="popup__body">
            <ul className="popup__body__list">
              {isBluebikeStation ? (
                <>
                  <span className="popup__body__list__item popup__body__list__item--divider" />
                  <li className="popup__body__list__item popup__body__list__item--summary">
                    <Icon
                      icon="/img/icons/vehicle-types/bicycle.svg"
                      size="15px"
                      className="popup__body__list__item__icon"
                    />
                    <div className="popup__body__list__item__text">
                      <div className="popup__body__list__item__summary__headline">
                        <span className="popup__body__list__item__summary__total">
                          {properties.capacity}
                          {' '}
                          {formFactorText?.bicycle?.[properties.capacity === 1 ? 'single' : 'multi']}
                        </span>
                        <span className="popup__body__list__item__summary__available">
                          {totalAvailability}
                          {' '}
                          {t('popup_available')}
                        </span>
                      </div>
                      <ul className="popup__body__list__item__sublist">
                        {bluebikeVehicleTypes.map(vehicleType => (
                          <li key={vehicleType?.vehicle_type_id || vehicleType?.propulsion_type}>
                            <span className="popup__body__list__item__sublist__label">
                              {getBluebikeCountLabel(vehicleType)}
                            </span>
                            <span className="popup__body__list__item__sublist__count">
                              {vehicleType.availability}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                </>
              ) : vehicleTypes.map(vehicleType => (
                <>
                  <span className="popup__body__list__item popup__body__list__item--divider" />
                  {vehicleType?.form_factor && (
                    <li className="popup__body__list__item">
                      <Icon
                        icon={`/img/icons/vehicle-types/${vehicleType?.form_factor}.svg`}
                        size="15px"
                        className="popup__body__list__item__icon"
                      />
                      <p className="popup__body__list__item__text">
                        {vehicleType?.capacity > 1 && `${vehicleType.capacity} `}
                        {formFactorText?.[vehicleType?.form_factor]?.[vehicleType?.capacity === 1 ? 'single' : 'multi']}
                        {vehicleType?.propulsion_type
                          && ` (${[...propulsionOptions, ...propulsionNotUsedOptions].find(ff => (
                            ff.id === vehicleType?.propulsion_type))?.label || vehicleType?.propulsion_type}${vehicleType?.vehicle_accessories?.map((va, i) => (
                            `${i === 0 ? ',' : ' & '} ${transmissionOptions.find(to => to.id === va)?.label}`
                          )) || ''})`}
                        {(vehicleType?.propulsion_type || vehicleType?.class) && (
                          <span>
                            {vehicleType?.make && `${vehicleType?.make}`}
                            {vehicleType?.model && `${vehicleType?.make ? ' ' : ''}${vehicleType?.model}`}
                            {vehicleType?.class && `${vehicleType?.make || vehicleType?.model ? ' | ' : ''}${vehicleType?.class}`}
                          </span>
                        )}
                        {(vehicleType?.availability >= 0) && (
                          <span>
                            {vehicleType.availability}
                            {` ${t('popup_available')}`}
                          </span>
                        )}
                      </p>
                    </li>
                  )}
                  {(properties?.current_fuel_percent || properties?.current_range_meters
                    || vehicleType?.max_range_meters) && (
                      <li className="popup__body__list__item">
                        <Icon
                          icon={['electric', 'electric_assist', 'hybrid', 'plug-in-hybrid'].includes(vehicleType?.propulsion_type) ? faBatteryBolt : faGasPump}
                          size="14px"
                          className="popup__body__list__item__icon"
                        />
                        <p className="popup__body__list__item__text">
                          {properties?.current_range_meters ? `${(formatNumber((properties?.current_range_meters || 0) / 1000))}` : ''}
                          {!properties?.current_range_meters && vehicleType?.max_range_meters ? 'max ' : ''}
                          {vehicleType?.max_range_meters && `${properties?.current_range_meters ? ' km / ' : ''}${(formatNumber((vehicleType?.max_range_meters || 0) / 1000))} km`}
                          {(!properties?.current_range_meters && vehicleType?.max_range_meters && properties?.operator === 'villo') ? ` ${t('popup_villo_extra_range_meters')}` : ''}
                        </p>
                      </li>
                  )}
                  {(vehicleType?.return_constraint) && (
                    <li className="popup__body__list__item">
                      <Icon
                        icon={getReturnIcon(vehicleType?.return_constraint)}
                        size="14px"
                        className="popup__body__list__item__icon"
                      />
                      <p className="popup__body__list__item__text">
                        {`${t('popup_return-option_title')}: `}
                        {returnContraintsText?.[vehicleType?.form_factor]
                          ?.[vehicleType?.return_constraint]?.[vehicle ? 'free' : 'station']
                          || returnContraintsText?.[vehicleType?.form_factor]
                            ?.[vehicleType?.return_constraint]}
                      </p>
                    </li>
                  )}
                  {vehicleType?.vehicle_image && (
                    <li className="popup__body__list__item popup__body__list__item--image">
                      <img
                        src={vehicleType?.vehicle_image}
                        className="popup__body__list__item__image"
                      />
                    </li>
                  )}
                  {vehicleType?.for_driving_lessons && (
                    <li className="popup__body__list__item">
                      <Icon
                        icon={faGraduationCap}
                        size="15px"
                        className="popup__body__list__item__icon"
                      />
                      <p className="popup__body__list__item__text">
                        {t('popup_driving_lessons')}
                      </p>
                    </li>
                  )}
                  {vehicleType?.pricingPlan && (
                    <li className="popup__body__list__item popup__body__list__item--icon-top">
                      <Icon
                        icon={faEuroSign}
                        size="15px"
                        className="popup__body__list__item__icon"
                      />
                      <p className="popup__body__list__item__text">
                        {vehicleType?.pricingPlan?.name ? `${t('popup_pricing-plan')}: ${vehicleType?.pricingPlan?.name}` : ''}
                        {vehicleType?.pricingPlan?.length && !vehicleType?.pricingPlan?.[0]?.per_min_pricing ? `${t('popup_pricing-plan')}: ${vehicleType?.pricingPlan.map(pp => pp.name).join(', ')}` : ''}
                        {vehicleType?.pricingPlan?.length && vehicleType?.pricingPlan?.map((pp) => {
                          if (pp?.per_min_pricing?.length === 1) {
                            return (
                              <>
                                {pp?.price ? `${pp?.price} EUR + ` : ''}
                                {pp?.per_min_pricing.map((pmp) => {
                                  return (
                                    `${pmp?.price ? `${pmp.price} EUR + ` : ''}${pmp?.rate} EUR /`
                                    + `${pmp?.interval !== 1 && pmp?.interval !== 60 ? ` ${pmp.interval} ${t('popup_pricing-plan_minute')}` : ''}`
                                    + `${pmp?.interval === 1 ? ` ${t('popup_pricing-plan_minute')}` : ''}`
                                    + `${pmp?.interval === 60 ? ` ${t('popup_pricing-plan_hour')}` : ''}`
                                  )
                                }).join(', ')}
                              </>
                            );
                          }

                          if (pp?.per_min_pricing?.length > 1) {
                            return (
                              <>
                                {pp?.name ? (
                                  <p className="popup__body__list__item__text__title">
                                    {`${t('popup_pricing-plan')}:`}
                                  </p>
                                ) : ''}
                                <ul>
                                  {pp?.per_min_pricing.map((pmp, i) => {
                                    if (i === 0) return <li>{`${t('popup_pricing-plan_less-than')} ${pmp?.end} ${t('popup_pricing-plan_minute')}: ${formatNumber(pmp?.rate, 2)} EUR`}</li>;
                                    if (i === pp.per_min_pricing.length - 1) {
                                      let totalRate = pp?.per_min_pricing.slice(0, i)
                                        .map(({ rate }) => Number(rate));
                                      totalRate = totalRate.reduce((a, cv) => a + cv, 0);
                                      const startTime = minutesToNiceString(pmp.start, t);

                                      return (
                                        <li>
                                          {`${t('popup_pricing-plan_more-than')} `}
                                          {startTime}
                                          {`: ${formatNumber(totalRate, 2)} EUR`}
                                          {` + ${pmp?.rate} EUR / ${t('popup_pricing-plan_day')}`}
                                        </li>
                                      );
                                    }

                                    let totalRate = pp?.per_min_pricing.slice(0, i + 1)
                                      .map(({ rate }) => Number(rate));
                                    totalRate = totalRate.reduce((a, cv) => a + cv, 0);

                                    const startTime = minutesToNiceString(pmp.start, t);
                                    const endTime = minutesToNiceString(pmp.end, t);

                                    return (
                                      <li>
                                        {startTime}
                                        {' - '}
                                        {endTime}
                                        {`: ${formatNumber(totalRate, 2)} EUR`}
                                      </li>
                                    )
                                  })}
                                </ul>
                              </>
                            );
                          }

                          return '';
                        })}
                      </p>
                    </li>
                  )}
                </>
              ))}
              <span className="popup__body__list__item popup__body__list__item--divider" />
              {properties?.partner && (
                <li className="popup__body__list__item">
                  <Icon
                    icon={faHandshake}
                    size="15px"
                    className="popup__body__list__item__icon"
                  />
                  <p className="popup__body__list__item__text">
                    {`${t('popup_partner_title')}: `}
                    {properties.partner}
                  </p>
                </li>
              )}
              {properties?.rental_uris && (
                <li className="popup__body__list__item popup__body__list__item--download">
                  <div className="popup__body__list__item__apps">
                    {properties?.rental_uris?.android && (
                      <Button
                        icon="/img/logo/android.svg"
                        className="popup__body__list__item__apps__app"
                        href={properties?.rental_uris?.android}
                        rootElProps={{ target: '_blank' }}
                      />
                    )}
                    {properties?.rental_uris?.ios && (
                      <Button
                        icon="/img/logo/ios.svg"
                        className="popup__body__list__item__apps__app"
                        href={properties?.rental_uris?.ios}
                        rootElProps={{ target: '_blank' }}
                      />
                    )}
                  </div>
                </li>
              )}
            </ul>
          </div>
          <div className="popup__footer">
            <Button
              className="popup__footer__button"
              href={url || ''}
              rootElProps={{ target: '_blank' }}
              onClick={(() => {
                if (window?.gtag) {
                  window.gtag('event', 'provider_link_clicked', {
                    event_category: 'Provider',
                    event_label: url,
                    provider_name: providersOptions.find(
                      ({ id }) => id === properties?.operator,
                    )?.label,
                    provider_url: url,
                  });
                }
              })}
            >
              {t('popup_learn-more_button')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default Popup;